import React from "react"
import { useMutation } from "react-query"
import serialize from "form-serialize"
import {
	MediaContent,
	Button,
	Flex,
	TextField,
	Text,
} from "@capioweb/ui-components/src"
import {
	StringProperty,
	LinkListProperty,
} from "../../@types/EpiFoundationSpa/Property"
import {
	QBankImage,
	XhtmlString,
	AppCtaLinks,
} from "../../@types/CapioWeb/core"
import { BlockCommonLayout } from "../../utils/layout"
import { sendSmsProMsg, ResultHash, SmsProResponseData } from "../../api"
import { LocalesContext } from "../../context"
import { sendAppDownloadDataLayerPush } from "../../utils/piwikPro"
import { imageBlockDataToImageProps } from "../../utils"

interface CapioNexusSearchCareInAppBlockProps {
	heading: StringProperty
	image: QBankImage
	link: LinkListProperty
	preamble: XhtmlString
	smsContentText: StringProperty
	validationPattern?: string
	ctaUrl: AppCtaLinks
}

const CapioNexusSearchCareInAppBlock: React.FC<CapioNexusSearchCareInAppBlockProps> =
	({ image, link, heading, preamble, ...rest }) => {
		const { translate, translationKeys } = React.useContext(LocalesContext)
		const [submitDisabled, setSubmitDisabled] = React.useState(true)
		const [feedbackMessage, setFeedbackMessage] = React.useState(null)
		const [genericErrorMessages, setGenericErrorMessages] = React.useState([])
		const [phoneFieldErrorMessage, setPhoneFieldErrorMessage] =
			React.useState(null)

		const formRef = React.useRef<HTMLFormElement>(null)
		const mutation = useMutation((body: ResultHash) => sendSmsProMsg(body))

		const onSubmit = (e: React.FormEvent) => {
			e.preventDefault()

			if (formRef.current) {
				const data = serialize(formRef.current, { hash: true })

				sendAppDownloadDataLayerPush("phonenumber")

				mutation.mutate(data)
			}
		}

		React.useEffect(() => {
			if (mutation.isIdle) return

			const handleResponse = async () => {
				if (mutation.data) {
					setFeedbackMessage(null)
					setPhoneFieldErrorMessage(null)
					setGenericErrorMessages([])

					if (mutation.isSuccess && mutation.data?.status === 200) {
						const data: SmsProResponseData = await mutation.data.json()
						setFeedbackMessage(data.result)
					} else {
						const data: SmsProResponseData = await mutation.data.json()

						if (data.result) setFeedbackMessage(data.result)
						if (data.errors?.length > 0) {
							const phoneFieldErrors = []
							const genericErrors = []

							data.errors.forEach((item) => {
								if (item.key === "phone") {
									phoneFieldErrors.push(item.message)
								} else {
									genericErrors.push(item.message)
								}
							})
							setGenericErrorMessages(genericErrors)
							setPhoneFieldErrorMessage(phoneFieldErrors.join(" "))
						}
					}
				}
			}

			handleResponse().catch((e) => console.error(e))
		}, [mutation.isSuccess, mutation.isError])

		return (
			<BlockCommonLayout heading={heading}>
				<MediaContent
					mirror={true}
					media={
						<img
							{...imageBlockDataToImageProps(image, true, true, [
								{ width: 1000, breakpoint: 2560 },
							])}
						/>
					}
					cta={
						<>
							{/* Form - for screens > l */}
							<Flex
								ref={formRef}
								display={{ _: "none", l: "flex" }}
								flexDirection="column"
								w={1}
								as="form"
								onSubmit={onSubmit}
							>
								<Flex className="visually-hidden">
									<TextField
										id="smsProMessageField"
										name="message"
										label=""
										defaultValue={rest.smsContentText.value}
										hidden
									/>
								</Flex>
								<Flex>
									<TextField
										id="smsProPhoneField"
										name="phone"
										error={phoneFieldErrorMessage && phoneFieldErrorMessage}
										onChange={(e: React.FormEvent<HTMLInputElement>) =>
											setSubmitDisabled(
												e.currentTarget.value.length > 0 ? false : true
											)
										}
										label={translate(translationKeys.searchCareInAppLabel)}
										helperText={phoneFieldErrorMessage ?? feedbackMessage}
										placeholder="+46 7XX XX XX XX"
									/>
									<TextField
										id="smsProCountryCodeField"
										name="phoneCountryCode"
										label={translate(translationKeys.searchCareInAppLabel)}
										helperText={feedbackMessage}
										placeholder="+46"
										isHoneypot={true}
									/>
								</Flex>
								<Flex>
									<Button
										type="submit"
										display={{ _: "none", l: "inline-flex" }}
										disabled={submitDisabled}
									>
										{translate(translationKeys.searchCareInAppSubmitLabel)}
									</Button>
								</Flex>
								{genericErrorMessages?.length > 0 &&
									genericErrorMessages.map((error, i) => {
										if (error.key !== "phone") {
											return (
												<Flex w={1} mt={1.5} key={i + error.key}>
													<Text
														as="span"
														textColor="alert"
														variant="paragraph-14"
														mb={0}
													>
														{error}
													</Text>
												</Flex>
											)
										}
									})}
							</Flex>

							{/* CTAs - for screens < l */}
							<Flex display={{ l: "none" }} flexDirection="column">
								<Button.Anchor
									display="block"
									onClick={() => sendAppDownloadDataLayerPush("ios")}
									href={rest.ctaUrl.appleStoreCTALink.href}
									mb={1}
								>
									{translate(translationKeys.appStore)}
								</Button.Anchor>
								<Button.Anchor
									display="block"
									onClick={() => sendAppDownloadDataLayerPush("android")}
									href={rest.ctaUrl.googlePlayStoreCTALink.href}
								>
									{translate(translationKeys.googlePlay)}
								</Button.Anchor>
							</Flex>
						</>
					}
				>
					{preamble.value && <MediaContent.RteContent text={preamble.value} />}
				</MediaContent>
			</BlockCommonLayout>
		)
	}

export { CapioNexusSearchCareInAppBlock }
